import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ReminderPanel.css';

interface Reminder {
  id: number;
  message: string;
  remind_at: string;
  is_completed: boolean;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  snooze_until: string | null;
  snoozed_count: number;
  source: string;
  relative_time?: string;
  urgency?: 'urgent' | 'soon' | 'normal';
  created_at: string;
}

interface ReminderPanelProps {
  apiUrl: string;
  userToken: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const ReminderPanel: React.FC<ReminderPanelProps> = ({ apiUrl, userToken, isOpen, onClose }) => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [snoozeMenu, setSnoozeMenu] = useState<number | null>(null);

  const [newReminder, setNewReminder] = useState({
    message: '',
    date: '',
    time: '',
    is_recurring: false,
    recurrence_pattern: ''
  });

  useEffect(() => {
    if (isOpen && userToken) {
      fetchReminders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userToken]);

  const fetchReminders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiUrl}/reminders/upcoming`, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: { limit: 20 }
      });
      setReminders(response.data.reminders || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async () => {
    if (!newReminder.message.trim() || !newReminder.date || !newReminder.time) return;

    try {
      // Create datetime with timezone offset so backend knows user's local time
      const localDate = new Date(`${newReminder.date}T${newReminder.time}:00`);
      const triggerTime = localDate.toISOString(); // Converts to UTC with Z suffix

      // Determine repeat_type from recurrence_pattern
      let repeatType = "once";
      if (newReminder.is_recurring && newReminder.recurrence_pattern) {
        repeatType = newReminder.recurrence_pattern; // "daily", "weekly", "monthly"
      }

      const payload = {
        message: newReminder.message,
        trigger_time: triggerTime,
        repeat_type: repeatType
      };
      console.log('Creating reminder with payload:', payload);

      const response = await axios.post(`${apiUrl}/reminders`, payload, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Reminder created:', response.data);

      setNewReminder({ message: '', date: '', time: '', is_recurring: false, recurrence_pattern: '' });
      setShowAddForm(false);
      fetchReminders();
    } catch (err: any) {
      console.error('Reminder creation failed:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Failed to add reminder');
    }
  };

  const handleUpdateReminder = async () => {
    if (!editingReminder) return;

    try {
      await axios.put(`${apiUrl}/reminders/${editingReminder.id}`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: {
          message: editingReminder.message,
          remind_at: editingReminder.remind_at
        }
      });
      setEditingReminder(null);
      fetchReminders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update reminder');
    }
  };

  const handleCompleteReminder = async (reminderId: number) => {
    try {
      await axios.put(`${apiUrl}/reminders/${reminderId}`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: { is_completed: true }
      });
      fetchReminders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete reminder');
    }
  };

  const handleSnoozeReminder = async (reminderId: number, minutes: number) => {
    try {
      await axios.post(`${apiUrl}/reminders/${reminderId}/snooze`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: { snooze_minutes: minutes }
      });
      setSnoozeMenu(null);
      fetchReminders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to snooze reminder');
    }
  };

  const handleDeleteReminder = async (reminderId: number) => {
    try {
      await axios.delete(`${apiUrl}/reminders/${reminderId}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      setDeleteConfirm(null);
      fetchReminders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete reminder');
    }
  };

  const getUrgencyClass = (urgency?: string) => {
    switch (urgency) {
      case 'urgent': return 'urgency-urgent';
      case 'soon': return 'urgency-soon';
      default: return 'urgency-normal';
    }
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="reminder-panel-overlay" onClick={onClose} />
      <div className={`reminder-panel ${isOpen ? 'open' : ''}`}>
        <div className="reminder-header">
          <h3>⏰ Reminders</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="reminder-actions-top">
          <button
            className="add-reminder-btn"
            onClick={() => setShowAddForm(true)}
          >
            + New Reminder
          </button>
        </div>

        {loading && <div className="reminder-loading">Loading...</div>}
        {error && <div className="reminder-error">{error}</div>}

        {!loading && reminders.length === 0 && (
          <div className="reminder-empty">
            <p>No upcoming reminders</p>
            <small>Type "remind me to..." in chat or add one here</small>
          </div>
        )}

        {/* Reminder List */}
        <div className="reminder-list">
          {reminders.map(reminder => (
            <div
              key={reminder.id}
              className={`reminder-card ${getUrgencyClass(reminder.urgency)}`}
            >
              {editingReminder?.id === reminder.id ? (
                <div className="reminder-edit-form">
                  <textarea
                    value={editingReminder.message}
                    onChange={(e) => setEditingReminder({ ...editingReminder, message: e.target.value })}
                    className="edit-textarea"
                  />
                  <div className="edit-actions">
                    <button onClick={handleUpdateReminder} className="save-btn">Save</button>
                    <button onClick={() => setEditingReminder(null)} className="cancel-btn">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="reminder-content">
                    <div className="reminder-message">{reminder.message}</div>
                    <div className="reminder-meta">
                      <span className={`reminder-time ${getUrgencyClass(reminder.urgency)}`}>
                        {reminder.relative_time || formatDateTime(reminder.remind_at)}
                      </span>
                      {reminder.is_recurring && (
                        <span className="recurring-badge">🔁 {reminder.recurrence_pattern}</span>
                      )}
                      {reminder.snoozed_count > 0 && (
                        <span className="snoozed-badge">😴 Snoozed {reminder.snoozed_count}x</span>
                      )}
                    </div>
                  </div>

                  <div className="reminder-actions-row">
                    <button
                      onClick={() => handleCompleteReminder(reminder.id)}
                      className="action-btn-small complete"
                      title="Mark complete"
                    >
                      ✓
                    </button>

                    <div className="snooze-container">
                      <button
                        onClick={() => setSnoozeMenu(snoozeMenu === reminder.id ? null : reminder.id)}
                        className="action-btn-small"
                        title="Snooze"
                      >
                        😴
                      </button>
                      {snoozeMenu === reminder.id && (
                        <div className="snooze-menu">
                          <button onClick={() => handleSnoozeReminder(reminder.id, 5)}>5 min</button>
                          <button onClick={() => handleSnoozeReminder(reminder.id, 15)}>15 min</button>
                          <button onClick={() => handleSnoozeReminder(reminder.id, 60)}>1 hour</button>
                          <button onClick={() => handleSnoozeReminder(reminder.id, 1440)}>1 day</button>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => setEditingReminder(reminder)}
                      className="action-btn-small"
                      title="Edit"
                    >
                      ✏️
                    </button>

                    <button
                      onClick={() => setDeleteConfirm(reminder.id)}
                      className="action-btn-small delete"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Reminder Form Modal */}
        {showAddForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h4>New Reminder</h4>

              <textarea
                placeholder="What do you want to be reminded about?"
                value={newReminder.message}
                onChange={(e) => setNewReminder({ ...newReminder, message: e.target.value })}
                className="form-textarea"
              />

              <div className="datetime-row">
                <input
                  type="date"
                  value={newReminder.date}
                  onChange={(e) => setNewReminder({ ...newReminder, date: e.target.value })}
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
                <input
                  type="time"
                  value={newReminder.time}
                  onChange={(e) => setNewReminder({ ...newReminder, time: e.target.value })}
                  className="form-input"
                />
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newReminder.is_recurring}
                  onChange={(e) => setNewReminder({ ...newReminder, is_recurring: e.target.checked })}
                />
                Recurring reminder
              </label>

              {newReminder.is_recurring && (
                <select
                  value={newReminder.recurrence_pattern}
                  onChange={(e) => setNewReminder({ ...newReminder, recurrence_pattern: e.target.value })}
                  className="form-select"
                >
                  <option value="">Select frequency</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              )}

              <div className="modal-actions">
                <button onClick={handleAddReminder} className="primary-btn">Create Reminder</button>
                <button onClick={() => setShowAddForm(false)} className="secondary-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="confirm-modal">
            <div className="confirm-content">
              <p>Delete this reminder?</p>
              <div className="confirm-actions">
                <button
                  className="confirm-yes"
                  onClick={() => handleDeleteReminder(deleteConfirm)}
                >
                  Delete
                </button>
                <button
                  className="confirm-no"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReminderPanel;
